// Answers saved on the family's device while they fill in a form ("device copies").
//
// Rules (reviewer decision, 20 Oct 2026):
// - A device copy belongs to one form AND one person: it is keyed by form + ref (or agreement token) + client code.
//   A link for a different client never sees another client's answers.
// - If the link carries no ref, token or code, nothing is resumed until the family has seen
//   "Answers started on <date> for <first name>" and chosen Continue or Start fresh.
// - Device copies expire 7 days after they were last saved, and are cleared the next time the portal opens.
// - Only the family's own choice clears a copy, and clearing one form never touches another form's copy.
// - None of this touches the copy saved on the server.

export const PREFIX = "cm_draft_v2::";
export const LEGACY_PREFIX = "assessment_progress_";
export const TTL_MS = 7 * 24 * 60 * 60 * 1000;

function defaultStorage() {
  try {
    return window.localStorage;
  } catch (e) {
    return null;
  }
}

// Small, stable, non-cryptographic hash (cyrb53) so refs and tokens are not written into storage key names as-is.
export function hashId(str) {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, "0");
}

function norm(v) {
  return String(v || "").trim();
}

// The identity a device copy is filed under. `linkKeyed` says whether the LINK itself named the person
// (ref, agreement token or code in the link). When it didn't, the family must confirm before resuming.
export function draftIdentity({ form, ref, token, code, linkKeyed }) {
  const r = norm(ref);
  const t = norm(token);
  const c = norm(code).toUpperCase();
  const key = `${PREFIX}${form}::${hashId(JSON.stringify([r, t, c]))}`;
  return { key, form, keyed: !!(r || t || c), linkKeyed: !!linkKeyed };
}

function readRaw(storage, key) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function lastSaved(data) {
  const t = Date.parse((data && (data.savedAt || data.startedAt)) || "");
  return Number.isFinite(t) ? t : 0;
}

export function isExpired(data, now = Date.now()) {
  return !data || now - lastSaved(data) > TTL_MS;
}

export function saveDeviceDraft(key, data, { storage = defaultStorage(), now = Date.now() } = {}) {
  if (!storage || !key) return false;
  try {
    const existing = readRaw(storage, key);
    const nowIso = new Date(now).toISOString();
    const startedAt = (existing && !isExpired(existing, now) && existing.startedAt) || data.startedAt || nowIso;
    storage.setItem(key, JSON.stringify({ ...data, startedAt, savedAt: nowIso }));
    return true;
  } catch (e) {
    return false;
  }
}

export function loadDeviceDraft(key, { storage = defaultStorage(), now = Date.now() } = {}) {
  if (!storage || !key) return null;
  const data = readRaw(storage, key);
  if (!data) return null;
  if (isExpired(data, now)) {
    clearDeviceDraft(key, { storage });
    return null;
  }
  return data;
}

// Clears exactly one form's device copy for one person.
export function clearDeviceDraft(key, { storage = defaultStorage() } = {}) {
  if (!storage || !key) return;
  try {
    storage.removeItem(key);
  } catch (e) {
    /* nothing to do */
  }
}

function allKeys(storage) {
  const keys = [];
  try {
    for (let i = 0; i < storage.length; i++) keys.push(storage.key(i));
  } catch (e) {
    /* storage unavailable */
  }
  return keys.filter(Boolean);
}

// Run once when the portal opens: drop expired copies, and move copies saved before this change (filed by form
// only) under the per-person key built from the ref and code they carried.
export function sweepDeviceDrafts({ storage = defaultStorage(), now = Date.now() } = {}) {
  if (!storage) return { removed: 0, migrated: 0 };
  let removed = 0;
  let migrated = 0;
  for (const k of allKeys(storage)) {
    if (k.startsWith(PREFIX)) {
      if (isExpired(readRaw(storage, k), now)) {
        clearDeviceDraft(k, { storage });
        removed += 1;
      }
    } else if (k.startsWith(LEGACY_PREFIX)) {
      const data = readRaw(storage, k);
      clearDeviceDraft(k, { storage });
      if (!data || isExpired(data, now)) {
        removed += 1;
        continue;
      }
      const form = k.slice(LEGACY_PREFIX.length);
      const { key } = draftIdentity({ form, ref: data.ref, token: "", code: data.clientCode });
      if (!readRaw(storage, key)) {
        try {
          storage.setItem(key, JSON.stringify({ ...data, startedAt: data.startedAt || data.savedAt }));
          migrated += 1;
        } catch (e) {
          /* storage full: drop it */
        }
      }
    }
  }
  return { removed, migrated };
}

// The first name shown on the "Answers started on ... for ..." screen.
export function firstNameOf(data) {
  const r = (data && data.responses) || {};
  const full = norm(data && data.name) || norm(r.first_name) || norm(r.client_first_name) || norm(data && data.completedByName);
  return full.split(/\s+/)[0] || "";
}

export function startedOnText(data) {
  const t = Date.parse((data && (data.startedAt || data.savedAt)) || "");
  if (!Number.isFinite(t)) return "";
  try {
    return new Date(t).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  } catch (e) {
    return new Date(t).toDateString();
  }
}
